import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { PX_PER_PT } from '../../../../editor-geometry';

interface Tick {
  pos: number; // px along the ruler
  length: number; // px
  label: string | null;
}

/** A centimetre ruler along one edge of the page, at the current zoom. */
@Component({
  selector: 'app-page-ruler',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (orientation() === 'horizontal') {
      <svg [attr.width]="lengthPx()" height="18" class="block overflow-visible" aria-hidden="true">
        <line x1="0" [attr.x2]="lengthPx()" y1="17.5" y2="17.5" stroke="currentColor" />
        @for (tick of ticks(); track tick.pos) {
          <line
            [attr.x1]="tick.pos"
            [attr.x2]="tick.pos"
            [attr.y1]="18 - tick.length"
            y2="18"
            stroke="currentColor" />
          @if (tick.label) {
            <text [attr.x]="tick.pos + 2" y="9" font-size="9" fill="currentColor">
              {{ tick.label }}
            </text>
          }
        }
      </svg>
    } @else {
      <svg width="18" [attr.height]="lengthPx()" class="block overflow-visible" aria-hidden="true">
        <line y1="0" [attr.y2]="lengthPx()" x1="17.5" x2="17.5" stroke="currentColor" />
        @for (tick of ticks(); track tick.pos) {
          <line
            [attr.y1]="tick.pos"
            [attr.y2]="tick.pos"
            [attr.x1]="18 - tick.length"
            x2="18"
            stroke="currentColor" />
          @if (tick.label) {
            <text x="1" [attr.y]="tick.pos + 10" font-size="9" fill="currentColor">
              {{ tick.label }}
            </text>
          }
        }
      </svg>
    }
  `,
  host: { class: 'text-muted-foreground block select-none' },
})
export class PageRulerComponent {
  readonly lengthPt = input.required<number>();
  readonly scale = input.required<number>();
  readonly orientation = input<'horizontal' | 'vertical'>('horizontal');

  readonly lengthPx = computed(() => this.lengthPt() * PX_PER_PT * this.scale());

  readonly ticks = computed<Tick[]>(() => {
    const pxPerMm = (PX_PER_PT * this.scale() * 72) / 25.4;
    const lengthMm = (this.lengthPt() * 25.4) / 72;
    // labels at least ~28 px apart: every 1, 2, 5 or 10 cm
    const labelCm = [1, 2, 5, 10, 20].find((cm) => cm * 10 * pxPerMm >= 28) ?? 50;
    const minorMm = pxPerMm >= 5 ? 1 : pxPerMm * 5 >= 5 ? 5 : 10;
    const ticks: Tick[] = [];
    for (let mm = 0; mm <= lengthMm + 1e-6; mm += minorMm) {
      const isCm = Math.abs(mm % 10) < 1e-6;
      const labelled = isCm && Math.abs((mm / 10) % labelCm) < 1e-6;
      ticks.push({
        pos: mm * pxPerMm,
        length: labelled ? 12 : isCm ? 8 : mm % 5 === 0 ? 6 : 3,
        label: labelled && mm > 0 ? String(mm / 10) : null,
      });
    }
    return ticks;
  });
}
